import type { FilterField } from './hooks/useListFilters';
import { SKU_STATUS_LABEL } from './skuMaster';
import {
  OUTBOUND_METHOD_LABEL,
  OUTBOUND_TYPE_META,
  OPNAME_TYPE_META,
  OUTBOUND_TYPES,
  PO_TYPE_META,
  PO_TYPES,
  SOURCE_META,
  STORAGE_TYPE_LABEL,
} from './status';
import type { OutboundOrderSource } from './types';

/**
 * "Filter (N)" fields per WMS menu (module-level: the URL hooks key on their
 * identity). URL params equal the API params, so a link to a filtered list
 * reads the same as the request it makes.
 */

const CLIENT: FilterField = { kind: 'client', label: 'Client' };
const VENDOR: FilterField = { kind: 'vendor', label: 'Vendor' };
const CREATED: FilterField = {
  kind: 'date',
  key: 'date',
  label: 'Tanggal dibuat',
  presets: 'past',
};
const ARRIVAL: FilterField = {
  kind: 'date',
  key: 'arrival',
  label: 'Estimasi tiba',
  presets: 'future',
};
const PO_TYPE: FilterField = {
  kind: 'enum',
  key: 'type',
  label: 'Tipe',
  options: PO_TYPES.map((t) => ({ value: t, label: PO_TYPE_META[t].label })),
};

export const OUTBOUND_SOURCES: OutboundOrderSource[] = [
  'MANUAL',
  'CSV',
  'CLIENT_API',
];

export const PO_FILTERS: readonly FilterField[] = [
  CLIENT,
  VENDOR,
  PO_TYPE,
  CREATED,
  ARRIVAL,
];

export const INBOUND_FILTERS: readonly FilterField[] = PO_FILTERS;

export const GOODS_RECEIPT_FILTERS: readonly FilterField[] = [
  CLIENT,
  VENDOR,
  { kind: 'date', key: 'date', label: 'Tanggal terima', presets: 'past' },
];

export const OUTBOUND_FILTERS: readonly FilterField[] = [
  CLIENT,
  {
    kind: 'enum',
    key: 'type',
    label: 'Tipe',
    options: OUTBOUND_TYPES.map((t) => ({
      value: t,
      label: OUTBOUND_TYPE_META[t].label,
    })),
  },
  {
    kind: 'enum',
    key: 'source',
    label: 'Sumber',
    options: OUTBOUND_SOURCES.map((s) => ({
      value: s,
      label: SOURCE_META[s].label,
    })),
  },
  {
    kind: 'enum',
    key: 'method',
    label: 'Metode',
    options: [{ value: 'DELIVERY', label: OUTBOUND_METHOD_LABEL.DELIVERY }],
  },
  { kind: 'city', label: 'Kota tujuan' },
  { kind: 'upload', label: 'Batch upload' },
  CREATED,
  {
    kind: 'date',
    key: 'delivery',
    label: 'Tanggal kirim',
    presets: 'future',
  },
];

/** Picking and Shipping. */
export const GOODS_ISSUE_FILTERS: readonly FilterField[] = [
  CLIENT,
  {
    kind: 'text',
    key: 'picker',
    label: 'Picker',
    placeholder: 'Nama picker…',
  },
  CREATED,
];

export const STOCK_FILTERS: readonly FilterField[] = [
  CLIENT,
  { kind: 'storage', label: 'Storage' },
  { kind: 'date', key: 'expiry', label: 'Kedaluwarsa', presets: 'future' },
];

export const STORAGE_FILTERS: readonly FilterField[] = [
  {
    kind: 'enum',
    key: 'category',
    label: 'Kategori',
    options: [
      { value: 'LOCATION', label: 'Location' },
      { value: 'SECTION', label: 'Section' },
      { value: 'BIN', label: 'Bin' },
    ],
  },
  {
    kind: 'enum',
    key: 'type',
    label: 'Tipe',
    options: Object.entries(STORAGE_TYPE_LABEL).map(([value, label]) => ({
      value,
      label,
    })),
  },
  {
    kind: 'enum',
    key: 'status',
    label: 'Status',
    options: [
      { value: 'ACTIVE', label: 'Active' },
      { value: 'INACTIVE', label: 'Inactive' },
    ],
  },
];

export const SKU_FILTERS: readonly FilterField[] = [
  CLIENT,
  {
    kind: 'enum',
    key: 'status',
    label: 'Status',
    options: [
      { value: 'ACTIVE', label: SKU_STATUS_LABEL.ACTIVE },
      { value: 'INACTIVE', label: SKU_STATUS_LABEL.INACTIVE },
    ],
  },
  {
    kind: 'text',
    key: 'productCategory',
    label: 'Product Category',
    placeholder: 'mis. Headstrap',
  },
];

/* transfer storage */

/** Transfer list: the source bin (URL `…ID` chain → API `storageID`) and the created date. */
export const TRANSFER_FILTERS: readonly FilterField[] = [
  { kind: 'storage', label: 'Storage asal' },
  CREATED,
];

/* transfer warehouse (hub → hub) */

/**
 * Hub transfer list: client, the two ends of the route, the requester and the
 * created date. The page's own hub (header switcher) + the Asal / Tujuan
 * toggle are separate query params (`hubID`, `side`), not filters.
 */
export const HUB_TRANSFER_FILTERS: readonly FilterField[] = [
  CLIENT,
  { kind: 'hub', key: 'sourceHubID', label: 'Hub asal' },
  { kind: 'hub', key: 'destinationHubID', label: 'Hub tujuan' },
  {
    kind: 'text',
    key: 'createdBy',
    label: 'Pemohon',
    placeholder: 'Email / nama pemohon…',
  },
  { kind: 'date', key: 'date', label: 'Tanggal pengajuan', presets: 'past' },
];

/* stock opname */

/** Opname list: client, type and created date (status is the tab). */
export const STOCK_OPNAME_FILTERS: readonly FilterField[] = [
  CLIENT,
  {
    kind: 'enum',
    key: 'type',
    label: 'Tipe',
    options: [
      {
        value: 'WITH_ADJUSTMENT',
        label: OPNAME_TYPE_META.WITH_ADJUSTMENT.label,
      },
      {
        value: 'WITHOUT_ADJUSTMENT',
        label: OPNAME_TYPE_META.WITHOUT_ADJUSTMENT.label,
      },
    ],
  },
  CREATED,
];
